(ns hai.home.desktop.mini-apps.setup
  "Tips and tricks mini-app card: the steps for getting Humble onto every
   surface, each stamped done in its own box. Sits under the clock on Home and
   wears the shared mini-app frame, its header in system gray. State and
   persistence live in [[hai.home.onboarding]]."
  (:require
   [hai.entity.kebab :as kebab]
   [hai.home.onboarding :as home-onboarding]
   [hai.mini-apps.frame :as mini-apps]
   [hai.onboarding :as onboarding]
   [hai.tasks.ui :as tasks-ui]
   [hai.ui.components :as c]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.ui.tokens :as t]
   [hai.web-app.auth :as auth]
   [lambdaisland.ornament :as o]
   [url.install :as install]))

(def ^:private lightbulb-icon (icons/mui-icon :outlined :lightbulb))
(def ^:private help-icon (icons/mui-icon :outlined :help_outline))
(def ^:private done-all-icon (icons/mui-icon :outlined :done_all))
(def ^:private expand-icon (icons/mui-icon :outlined :unfold_more))
(def ^:private collapse-icon (icons/mui-icon :outlined :unfold_less))

(def ^:private homepage-label-id "home-setup-homepage-label")

;; ---------------------------------------------------------------------------
;; Card frame
;; ---------------------------------------------------------------------------

(o/defstyled card mini-apps/card
  {:min-width "380px" :margin-top "16px"}
  ;; Duck tape on the right, so it doesn't line up with the clock's above.
  [:&::before {:left "auto" :right "40px"}]

  ;; Header: system gray rather than a content hue — this is Humble's own card,
  ;; not one of the user's apps.
  [:.app-header {:background t/--system}]
  ;; Collapsed, the header is the whole card, so it rounds its bottom corners
  ;; too — the frame leaves the card unclipped for the duck tape.
  [:&.collapsed
   [:.app-header
    {:border-bottom-left-radius "16px" :border-bottom-right-radius "16px"}]]
  ;; :lightbulb is a Material fill icon, unlike the stroke icons the shared card
  ;; frame assumes, so restore fill and match the other header icons' size.
  [:.app-icon [:svg {:fill "currentColor" :width "20px" :height "20px"}]]
  ;; Body padding lives here since the shared card frame leaves the body to each
  ;; mini-app (the header band supplies its own padding).
  [:.setup-body {:padding "16px 18px 18px"}]

  ;; The checklist's own heading, inside the body: dial and title stack.
  [:.setup-header
   {:display "flex" :align-items "center" :gap "12px"
    :padding-bottom "10px"}]
  ;; The conic fill comes from an inline style — it moves with progress.
  [:.setup-dial
   {:width "32px" :height "32px" :border-radius "50%"
    :flex "0 0 auto"
    :display "flex" :align-items "center" :justify-content "center"
    :transition "background 0.4s ease"}
   [:.dial-count
    {:width "22px" :height "22px" :border-radius "50%"
     :background t/--card-surface
     :display "flex" :align-items "center" :justify-content "center"
     :font-size "11px" :font-weight "700"
     :color t/--card-forest
     :font-variant-numeric "tabular-nums"}]]
  [:.setup-headings
   {:display "flex" :flex-direction "column" :gap "1px"}]
  [:.setup-title
   {:font-size "12px" :font-weight "700"
    :letter-spacing "0.1em" :text-transform "uppercase"
    :color t/--note-kraft
    :margin "0"}]
  [:.setup-subline
   {:font-size "12px" :color t/--link-muted}]

  ;; Step rows
  [:.setup-step
   {:display "flex" :align-items "center" :gap "12px"
    :padding "6px 0"}]
  ;; The shared stampbox is sized for a task row; a setup row is half that.
  [tasks-ui/stampbox
   {:width "24px" :height "24px" :border-radius "6px"}
   [tasks-ui/completion-stamp
    {:width "22px" :height "22px"}
    [:.check {:font-size "15px"}]]]
  [:.step-label
   {:font-size "15px" :font-weight "500"
    :color t/--card-forest
    :transition "color 0.2s ease"}]
  [:a.step-label
   {:display "inline-flex" :align-items "center" :gap "5px"
    :text-decoration "underline" :text-underline-offset "3px"
    :text-decoration-color t/--card-forest-14}
   [:&:hover {:color t/--note-kraft}]
   [:.step-arrow {:font-size "12px"}]]
  [:.setup-step.done
   [:.step-label
    {:color t/--link-muted
     :text-decoration "line-through"
     :text-decoration-color t/--card-forest-14}]]
  ;; Pushes the switch to the row's right edge.
  [:.step-spacer {:flex "1"}])

;; ---------------------------------------------------------------------------
;; Pieces
;; ---------------------------------------------------------------------------

(defn- subline-text
  [remaining]
  (case remaining
    0 "You’re all set"
    1 "One step left"
    (str remaining " steps left")))

(defn- dial
  [remaining]
  (let [total    (count onboarding/steps)
        complete (- total remaining)
        percent  (/ (* 100 complete) total)]
    [:div.setup-dial
     {:style {:background (str "conic-gradient(" (o/rvalue t/--task-accent) " 0 " percent "%, "
                               (o/rvalue t/--card-forest-10) " " percent "% 100%)")}}
     [:div.dial-count (if (zero? remaining) "✔" (str remaining))]]))

(defn- card-header
  [remaining collapsed?]
  [:div.app-header
   [:span.app-icon [lightbulb-icon]]
   [:h2.app-title "Tips and tricks"]
   [:div.app-actions
    [kebab/menu
     {:actions    [["Mark all as done"
                    #(>e [::home-onboarding/mark-all-done])
                    done-all-icon
                    (zero? remaining)]
                   ;; Reading the guide is not progress — this never stamps anything.
                   ["Setup help"
                    #(>e [:routing/navigate-in-new-tab install/setup-guide-url])
                    help-icon]]
      :aria-label "Tips and tricks actions"}]
    [:button.app-action-btn
     {:type       "button"
      :aria-label (if collapsed? "Expand tips and tricks" "Collapse tips and tricks")
      :title      (if collapsed? "Expand" "Collapse")
      :on-click   #(>e [::home-onboarding/toggle-collapsed])}
     (if collapsed? [expand-icon] [collapse-icon])]]])

(defn- setup-header
  [remaining]
  [:div.setup-header
   [dial remaining]
   [:div.setup-headings
    [:h3.setup-title "Humble setup"]
    [:span.setup-subline (subline-text remaining)]]])

(defn- step-row
  [{:keys [key label href]} done?]
  [:div.setup-step {:class (when done? "done")}
   [tasks-ui/stampbox
    {:done?      done?
     :task-title label
     :title      (if done? "Mark not done" "Mark done")
     :on-toggle  #(>e [::home-onboarding/toggle-step key])}]
   (if href
     [:a.step-label {:href href :target "_blank"}
      label
      [:span.step-arrow "↗"]]
     [:<>
      [:span.step-label {:id homepage-label-id} label]
      [:div.step-spacer]
      ;; The switch drives the real setting; the stamp beside it only records
      ;; that the user is finished with the step. Skipping is allowed, so
      ;; neither one moves the other.
      ;;
      ;; Named by the visible step rather than a separate `aria-label`, so the
      ;; accessible name and the on-screen text can't diverge.
      [c/Switch
       {:checked           @(<s [::home-onboarding/homepage-override?])
        :aria-labelledby   homepage-label-id
        :on-checked-change #(>e [::auth/set-newtab-override %])}]])])

;; ---------------------------------------------------------------------------
;; Panel
;; ---------------------------------------------------------------------------

(defn panel []
  (let [done       @(<s [::home-onboarding/done])
        remaining  @(<s [::home-onboarding/remaining])
        collapsed? @(<s [::home-onboarding/collapsed?])]
    [card {:class (when collapsed? "collapsed")}
     [card-header remaining collapsed?]
     (when-not collapsed?
       [:div.setup-body
        [setup-header remaining]
        [:div.setup-list
         (for [{:keys [key] :as step} onboarding/steps]
           ^{:key key} [step-row step (get done key)])]])]))
